/**
 * Resources Fragment authorizes against, and the actions defined on each.
 * The Permissions RFC is the source of truth for this inventory.
 *
 * Actions are resource-specific (not generic CRUD) so they map cleanly onto
 * real GraphQL operations and REST endpoints. The inventory is intentionally
 * coarse: `ledger:update` covers `updateLedger`, `updateLedgerAccount`, and
 * `updateLedgerEntry`. An action splits only when a real authorization
 * boundary emerges between operations that share a verb.
 *
 * Adding a resource or action requires an RFC update plus a matching entry
 * in `ROLE_PERMISSIONS`.
 */
export const RESOURCE_ACTIONS = {
  schema: ['store', 'delete', 'read'],
  ledger: ['create', 'update', 'delete', 'read', 'post_entry'],
  link: ['create', 'update', 'delete', 'read', 'sync'],
  workspace: ['update', 'read'],
  workspace_member: ['invite', 'delete', 'read', 'update'],
  api_client: ['create', 'delete', 'update', 'read'],
  data_export: ['create', 'delete', 'read'],
  custom_currency: ['create', 'read'],
  // Used for experimental features when you're not sure yet which permissions to use.
  experimental: ['experimental'],
  user: ['create', 'read', 'invite'],
} as const satisfies Record<string, readonly string[]>;

export type Resource = keyof typeof RESOURCE_ACTIONS;

export type Action<R extends Resource = Resource> =
  (typeof RESOURCE_ACTIONS)[R][number];

/**
 * The full set of `<resource>:<action>` permission strings derived from
 * `RESOURCE_ACTIONS`. Constructed via a mapped type so each resource is paired
 * only with the actions that resource defines — `Permission` cannot include
 * cross-resource combinations like `'ledger:invite'` (`invite` exists only on
 * `workspace_member`) or `'workspace:post_entry'` (`post_entry` exists only on
 * `ledger`).
 */
export type Permission = {
  [R in Resource]: `${R}:${Action<R>}`;
}[Resource];

/**
 * Runtime enumeration of every `Permission`. Iterate this rather than
 * re-deriving the union when building a `Record<Permission, T>`.
 */
export const PERMISSIONS: readonly Permission[] = Object.entries(
  RESOURCE_ACTIONS
).flatMap(([resource, actions]) =>
  actions.map((action) => `${resource}:${action}` as Permission)
);

// Compile error if `Permission` drifts from the catalog — without this, the
// `as Permission` cast in `PERMISSIONS` would silently launder a widened type.
type _DerivedPermission = {
  [R in keyof typeof RESOURCE_ACTIONS]: `${R}:${(typeof RESOURCE_ACTIONS)[R][number]}`;
}[keyof typeof RESOURCE_ACTIONS];
type _Equals<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;

const _permissionMatchesCatalog: _Equals<Permission, _DerivedPermission> = true;
